import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
  Dimensions,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import { Image } from 'react-native';
import Modal from '../components/Modal';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { StackScreenProps } from '@react-navigation/stack';
import { getProfileById, getProfileByCode, getApprovedProfilePhotos, viewContact, getMembershipStatus, sendInterest, getInterestStatus, addToShortlist, removeFromShortlist, getShortlistStatus, blockProfile, unblockProfile, getBlockStatus, reportProfile, getKundaliMatch, getProfileByUserId } from '../services/api';
import { StatusBadge, THEME } from '../components';
import { useAuthContext } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import type { Profile, ProfilePhoto, RootStackParamList, MembershipStatus, KundaliMatchResult } from '../types';

type Props = StackScreenProps<RootStackParamList, 'ProfileDetail'>;

const { width } = Dimensions.get('window');
const PHOTO_HEIGHT = width * 1.3;
const toHttps = (url?: string) => url && !url.startsWith('http') ? `https://${url}` : url;

// Normalise a value string into a translation-key-safe slug
const vk = (s: string) =>
  s.toLowerCase().replace(/[\s\/\-]+/g, '_').replace(/[^a-z0-9_]/g, '');

const incomeRange = (amount?: number | null): string | null => {
  if (!amount || amount <= 0) return null;
  const l = amount;
  if (l < 200000)   return 'Below 2 LPA';
  if (l < 500000)   return '2 – 5 LPA';
  if (l < 1000000)  return '5 – 10 LPA';
  if (l < 1500000)  return '10 – 15 LPA';
  if (l < 2000000)  return '15 – 20 LPA';
  if (l < 3000000)  return '20 – 30 LPA';
  if (l < 5000000)  return '30 – 50 LPA';
  return '50 LPA+';
};

interface InfoRowProps {
  icon: string;
  label: string;
  value?: string | null;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) =>
  value ? (
    <View style={styles.infoRow}>
      <Icon name={icon} size={18} color={THEME.primary} style={styles.infoIcon} />
      <View style={{ flex: 1 }}>
        <Text style={styles.infoLabel}>{label}</Text>
        <Text style={styles.infoValue}>{value}</Text>
      </View>
    </View>
  ) : null;

// ─── Photo Gallery ────────────────────────────────────────────────────────────

const PhotoGallery: React.FC<{ photos: ProfilePhoto[]; fallbackUrl?: string; blurred?: boolean }> = ({
  photos, fallbackUrl, blurred,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const displayPhotos: { id: string; uri: string }[] =
    photos.length > 0
      ? photos.map(p => ({ id: p.id, uri: toHttps(p.photoUrl) ?? '' }))
      : fallbackUrl
      ? [{ id: 'fallback', uri: fallbackUrl }]
      : [];

  if (displayPhotos.length === 0) {
    return (
      <View style={[styles.galleryContainer, styles.galleryPlaceholder]}>
        <Icon name="person-outline" size={64} color={THEME.border} />
      </View>
    );
  }

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const index = Math.round(e.nativeEvent.contentOffset.x / width);
    setActiveIndex(index);
  };

  return (
    <View style={styles.galleryContainer}>
      <FlatList
        data={displayPhotos}
        keyExtractor={item => item.id}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={16}
        renderItem={({ item }) => (
          <Image
            style={styles.galleryImage}
            source={{ uri: item.uri }}
            resizeMode="cover"
            blurRadius={blurred ? 18 : 0}
          />
        )}
      />

      {blurred && (
        <View style={styles.photoBlurOverlay}>
          <Icon name="lock-closed" size={32} color="#fff" />
          <Text style={styles.photoBlurText}>Accept interest to view photos</Text>
        </View>
      )}

      {!blurred && displayPhotos.length > 1 && (
        <>
          <View style={styles.dots}>
            {displayPhotos.map((_, i) => (
              <View key={i} style={[styles.dot, i === activeIndex && styles.dotActive]} />
            ))}
          </View>
          <View style={styles.photoCountBadge}>
            <Icon name="images-outline" size={12} color="#fff" />
            <Text style={styles.photoCountText}>{activeIndex + 1} / {displayPhotos.length}</Text>
          </View>
        </>
      )}
    </View>
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────

const ProfileDetailScreen: React.FC<Props> = ({ route, navigation }) => {
  const { profileId: profileIdParam, profileCode: profileCodeParam } = route.params;
  const { user } = useAuthContext();
  const { t } = useLanguage();
  const [profileId, setProfileId] = useState<string | undefined>(profileIdParam);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [photos, setPhotos] = useState<ProfilePhoto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [contactPhone, setContactPhone] = useState<string | null>(null);
  const [contactAltPhone, setContactAltPhone] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState<string | null>(null);
  const [loadingPhone, setLoadingPhone] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [membership, setMembership] = useState<MembershipStatus | null>(null);
  const [interestSent, setInterestSent] = useState(false);
  const [sendingInterest, setSendingInterest] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);
  const [togglingShortlist, setTogglingShortlist] = useState(false);
  const [viewLimitExceeded, setViewLimitExceeded] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);
  const [kundaliMatch, setKundaliMatch] = useState<KundaliMatchResult | null>(null);
  const [loadingKundali, setLoadingKundali] = useState(false);
  const [contactLinkedinName, setContactLinkedinName] = useState<string | null>(null);

  // Translate an enum-like stored value via MongoDB labels.
  // Falls back to the raw stored value if the key is not in DB yet.
  const tv = (prefix: string, value?: string | null): string | null => {
    if (value == null || value === '') return null;
    return t(`val.${prefix}.${vk(value)}`, value);
  };

  const bool = (v?: boolean | null): string | null =>
    v != null ? (v ? t('yes', 'Yes') : t('no', 'No')) : null;

  // Resolve profileCode → profileId when arriving from a deep link
  useEffect(() => {
    if (!profileId && profileCodeParam) {
      getProfileByCode(profileCodeParam)
        .then(res => setProfileId(res.data.id))
        .catch(() => Toast.show({ type: 'error', text1: 'Profile not found' }));
    }
  }, [profileCodeParam, profileId]);

  useEffect(() => {
    if (!profileId) return;
    Promise.all([
      getProfileById(profileId),
      getApprovedProfilePhotos(profileId).catch(() => ({ data: [] })),
      getMembershipStatus().catch(() => null),
      getShortlistStatus(profileId).catch(() => null),
      getInterestStatus(profileId).catch(() => ({ data: { sent: false } })),
      getBlockStatus(profileId).catch(() => ({ data: { blocked: false } })),
    ])
      .then(([profileRes, photosRes, membershipRes, shortlistRes, interestStatusRes, blockStatusRes]) => {
        setProfile(profileRes.data);
        const sorted = Array.isArray(photosRes.data)
          ? [...photosRes.data].sort((a: ProfilePhoto, b: ProfilePhoto) =>
              (b.mainPhoto ? 1 : 0) - (a.mainPhoto ? 1 : 0))
          : [];
        setPhotos(sorted);
        if (membershipRes) setMembership(membershipRes.data);
        if (shortlistRes) setShortlisted(shortlistRes.data.shortlisted);
        setInterestSent(interestStatusRes.data.sent ?? false);
        setBlocked(blockStatusRes.data.blocked ?? false);
      })
      .catch((e: unknown) => {
        const err = e as { status?: number; response?: { data?: { errorCode?: string } } };
        const status = err?.status ?? err?.response?.data;
        const errorCode = err?.response?.data?.errorCode;
        if (status === 402 && errorCode === 'PROFILE_VIEW_LIMIT_REACHED') {
          setViewLimitExceeded(true);
        } else {
          Toast.show({ type: 'error', text1: 'Failed to load profile' });
        }
      })
      .finally(() => setLoading(false));
  }, [profileId]);

  const isOwner = profile?.userId === user?.sub;

  // ⋮ header button — shown only for non-owners once profile loads
  useEffect(() => {
    if (!profile || isOwner) return;
    navigation.setOptions({
      headerRight: () => (
        <TouchableOpacity
          style={{ paddingHorizontal: 8, paddingVertical: 4 }}
          onPress={() => setShowOptionsModal(true)}>
          <Icon name="ellipsis-vertical" size={22} color={THEME.primary} />
        </TouchableOpacity>
      ),
    });
  }, [profile, isOwner, navigation]);

  const revealContact = async (field: 'phone' | 'email') => {
    if (!user?.sub || !profile) return;
    field === 'phone' ? setLoadingPhone(true) : setLoadingEmail(true);
    try {
      const res = await viewContact(profile.id);
      setContactPhone(res.data.contactNo ?? null);
      setContactAltPhone(res.data.otherContactNo ?? null);
      setContactEmail(res.data.email ?? null);
      setContactLinkedinName(res.data.linkedinName ?? null);
    } catch (e: any) {
      const status = e?.status ?? e?.response?.status;
      const errorCode = e?.response?.data?.errorCode;
      if (errorCode === 'CONTACT_PRIVACY_BLOCKED') {
        Toast.show({
          type: 'info',
          text1: 'Contact is private',
          text2: 'This profile shares contact only after accepting interest.',
        });
      } else if (status === 402 || status === 403) {
        Toast.show({
          type: 'error',
          text1: 'Membership required',
          text2: 'Upgrade your plan to view contact details',
        });
        navigation.navigate('Main');
      } else {
        Toast.show({ type: 'error', text1: 'Failed to load contact', text2: (e as Error).message });
      }
    } finally {
      setLoadingPhone(false);
      setLoadingEmail(false);
    }
  };

  const loadKundaliMatch = async () => {
    if (!user?.sub || !profile) return;
    setLoadingKundali(true);
    try {
      const myProfileRes = await getProfileByUserId(encodeURIComponent(user.sub));
      const result = await getKundaliMatch(myProfileRes.data.id, profile.id);
      setKundaliMatch(result.data);
    } catch (e) {
      console.error('[Kundali] load failed:', e);
      Toast.show({ type: 'error', text1: 'Could not load Kundali match' });
    } finally {
      setLoadingKundali(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={THEME.primary} />
      </View>
    );
  }

  if (viewLimitExceeded) {
    return (
      <View style={styles.center}>
        <Icon name="eye-off-outline" size={56} color={THEME.textLight} />
        <Text style={[styles.errorText, { marginTop: 16, textAlign: 'center', fontWeight: '700' }]}>
          Profile view limit reached
        </Text>
        <Text style={[styles.errorText, { fontSize: 14, marginTop: 8, textAlign: 'center', paddingHorizontal: 32 }]}>
          Upgrade your membership to continue viewing profiles
        </Text>
        <TouchableOpacity
          style={styles.upgradeBtn}
          onPress={() => navigation.navigate('Main')}>
          <Icon name="star-outline" size={16} color="#fff" />
          <Text style={styles.upgradeBtnText}>View Plans</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!profile) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>Profile not found</Text>
      </View>
    );
  }

  const showViewsRemaining = !isOwner
    && profile.viewsRemaining != null
    && profile.viewsLimit !== -1;
  const viewsLow = showViewsRemaining && (profile.viewsRemaining ?? 0) <= 3;

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>

      <PhotoGallery
        photos={photos}
        fallbackUrl={profile.photoUrl}
        blurred={!isOwner && !!profile.photoPrivacy && profile.connectedWithViewer === false}
      />

      {/* Views remaining banner */}
      {showViewsRemaining && (
        <View style={[styles.viewsBanner, viewsLow && styles.viewsBannerLow]}>
          <Icon
            name={viewsLow ? 'warning-outline' : 'eye-outline'}
            size={15}
            color={viewsLow ? '#92400E' : THEME.primary}
          />
          <Text style={[styles.viewsBannerText, viewsLow && styles.viewsBannerTextLow]}>
            {viewsLow
              ? `Only ${profile.viewsRemaining} profile view${profile.viewsRemaining === 1 ? '' : 's'} left — upgrade to see more`
              : `${profile.viewsRemaining} of ${profile.viewsLimit} profile views remaining`}
          </Text>
        </View>
      )}

      {/* Name card */}
      <View style={styles.nameCard}>
        <View style={styles.nameRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.name}>{profile.name}</Text>
            <Text style={styles.sub}>
              {[profile.district, profile.stateName].filter(Boolean).join(', ')}
            </Text>
            {profile.profileCode && (
              <Text style={styles.profileCode}>ID: {profile.profileCode}</Text>
            )}
          </View>
          <View style={styles.nameRowRight}>
            <StatusBadge status={profile.profileStatus} />
            {!isOwner && (
              <TouchableOpacity
                style={[styles.shortlistCheckbox, shortlisted && styles.shortlistCheckboxActive]}
                disabled={togglingShortlist}
                onPress={async () => {
                  setTogglingShortlist(true);
                  try {
                    if (shortlisted) {
                      await removeFromShortlist(profile.id);
                      setShortlisted(false);
                      Toast.show({ type: 'info', text1: 'Removed from shortlist' });
                    } else {
                      await addToShortlist(profile.id);
                      setShortlisted(true);
                      Toast.show({ type: 'success', text1: 'Added to shortlist!' });
                    }
                  } catch (e: unknown) {
                    const msg = (e as Error).message ?? '';
                    Toast.show({
                      type: 'error',
                      text1: msg.includes('limit') ? 'Shortlist full (50 max)' : 'Something went wrong',
                      text2: msg.includes('limit') ? 'Remove some profiles to add new ones.' : undefined,
                    });
                  } finally {
                    setTogglingShortlist(false);
                  }
                }}>
                {togglingShortlist
                  ? <ActivityIndicator size="small" color={shortlisted ? '#fff' : THEME.primary} />
                  : <Icon name={shortlisted ? 'checkmark' : 'add'} size={18} color={shortlisted ? '#fff' : THEME.primary} />
                }
                <Text style={[styles.shortlistCheckboxText, shortlisted && styles.shortlistCheckboxTextActive]}>
                  {shortlisted ? t('label.shortlisted', 'Shortlisted') : t('label.shortlist', 'Shortlist')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {isOwner && (
          <View style={styles.ownerBtns}>
            <TouchableOpacity
              style={styles.ownerBtn}
              onPress={() => navigation.navigate('EditProfile', { profile })}>
              <Icon name="pencil" size={16} color={THEME.primary} />
              <Text style={styles.ownerBtnText}>{t('btn.editProfile', 'Edit Profile')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.ownerBtn}
              onPress={() => navigation.navigate('Photo', { profileId: profile.id, currentPhoto: profile.photoUrl })}>
              <Icon name="camera" size={16} color={THEME.primary} />
              <Text style={styles.ownerBtnText}>{t('btn.updatePhoto', 'Update Photo')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {!isOwner && membership?.canSendInterest && (
          <TouchableOpacity
            style={[styles.interestBtn, interestSent && styles.interestBtnSent]}
            disabled={interestSent || sendingInterest}
            onPress={async () => {
              setSendingInterest(true);
              try {
                await sendInterest(profile.id);
                setInterestSent(true);
                Toast.show({ type: 'success', text1: 'Interest sent!' });
              } catch (e: any) {
                Toast.show({ type: 'error', text1: 'Could not send interest', text2: (e as Error).message });
              } finally {
                setSendingInterest(false);
              }
            }}>
            {sendingInterest
              ? <ActivityIndicator color="#fff" size="small" />
              : <>
                  <Icon name={interestSent ? 'checkmark-circle' : 'heart-outline'} size={18} color="#fff" />
                  <Text style={styles.interestBtnText}>
                    {interestSent ? t('label.interestSent', 'Interest Sent') : t('btn.sendInterest', 'Send Interest')}
                  </Text>
                </>
            }
          </TouchableOpacity>
        )}

        {!isOwner && !membership?.canSendInterest && (
          <TouchableOpacity
            style={styles.interestBtnLocked}
            onPress={() => navigation.navigate('Main')}>
            <Icon name="lock-closed-outline" size={16} color={THEME.textLight} />
            <Text style={styles.interestBtnLockedText}>{t('msg.membershipRequiredInterest', 'Membership required to send interest')}</Text>
          </TouchableOpacity>
        )}

        {/* ⋮ options modal — block/report via header button */}
        <Modal
          visible={showOptionsModal}
          transparent
          animationType="slide"
          onRequestClose={() => setShowOptionsModal(false)}>
          <TouchableOpacity
            style={styles.optionsOverlay}
            activeOpacity={1}
            onPress={() => setShowOptionsModal(false)}>
            <View style={styles.optionsSheet}>
              <View style={styles.optionsHandle} />

              <TouchableOpacity
                style={styles.optionsItem}
                disabled={blocking}
                onPress={async () => {
                  setShowOptionsModal(false);
                  setBlocking(true);
                  try {
                    if (blocked) {
                      await unblockProfile(profile.id);
                      setBlocked(false);
                      Toast.show({ type: 'info', text1: 'Profile unblocked' });
                    } else {
                      await blockProfile(profile.id);
                      setBlocked(true);
                      Toast.show({ type: 'success', text1: 'Profile blocked', text2: 'This profile will no longer appear in your feed.' });
                    }
                  } catch {
                    Toast.show({ type: 'error', text1: 'Something went wrong' });
                  } finally {
                    setBlocking(false);
                  }
                }}>
                {blocking
                  ? <ActivityIndicator size="small" color={THEME.textLight} />
                  : <Icon name={blocked ? 'ban' : 'ban-outline'} size={20} color={blocked ? THEME.primary : THEME.text} />}
                <Text style={[styles.optionsItemText, blocked && { color: THEME.primary }]}>
                  {blocked ? t('label.unblockProfile', 'Unblock Profile') : t('label.blockProfile', 'Block Profile')}
                </Text>
              </TouchableOpacity>

              <View style={styles.optionsDivider} />

              <TouchableOpacity
                style={styles.optionsItem}
                onPress={() => {
                  setShowOptionsModal(false);
                  setReportReason('');
                  setShowReportModal(true);
                }}>
                <Icon name="flag-outline" size={20} color={THEME.danger} />
                <Text style={[styles.optionsItemText, { color: THEME.danger }]}>
                  {t('label.reportProfile', 'Report Profile')}
                </Text>
              </TouchableOpacity>

              <View style={styles.optionsDivider} />

              <TouchableOpacity
                style={[styles.optionsItem, { justifyContent: 'center' }]}
                onPress={() => setShowOptionsModal(false)}>
                <Text style={styles.optionsCancelText}>{t('btn.cancel', 'Cancel')}</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      </View>

      {/* ── Report Modal ── */}
      <Modal
        visible={showReportModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowReportModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('label.reportProfile', 'Report Profile')}</Text>
            <Text style={styles.modalSubtitle}>{t('msg.reportReason', 'Why are you reporting this profile?')}</Text>

            {[
              { key: 'FAKE_PROFILE', label: t('report.fakeProfile', 'Fake or misleading profile') },
              { key: 'INAPPROPRIATE_CONTENT', label: t('report.inappropriate', 'Inappropriate content') },
              { key: 'HARASSMENT', label: t('report.harassment', 'Harassment or abuse') },
              { key: 'SPAM', label: t('report.spam', 'Spam or scam') },
              { key: 'OTHER', label: t('report.other', 'Other') },
            ].map(opt => (
              <TouchableOpacity
                key={opt.key}
                style={[styles.reportOption, reportReason === opt.key && styles.reportOptionSelected]}
                onPress={() => setReportReason(opt.key)}>
                <Icon
                  name={reportReason === opt.key ? 'radio-button-on' : 'radio-button-off'}
                  size={18}
                  color={reportReason === opt.key ? THEME.primary : THEME.textLight}
                />
                <Text style={[styles.reportOptionText, reportReason === opt.key && styles.reportOptionTextSelected]}>
                  {opt.label}
                </Text>
              </TouchableOpacity>
            ))}

            <View style={styles.modalBtns}>
              <TouchableOpacity
                style={styles.modalCancelBtn}
                onPress={() => setShowReportModal(false)}>
                <Text style={styles.modalCancelText}>{t('btn.cancel', 'Cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalSubmitBtn, !reportReason && styles.modalSubmitBtnDisabled]}
                disabled={!reportReason || reporting}
                onPress={async () => {
                  if (!reportReason) return;
                  setReporting(true);
                  try {
                    await reportProfile(profile.id, { reason: reportReason });
                    setShowReportModal(false);
                    Toast.show({ type: 'success', text1: 'Report submitted', text2: 'Thank you. Our team will review this profile.' });
                  } catch {
                    Toast.show({ type: 'error', text1: 'Could not submit report' });
                  } finally {
                    setReporting(false);
                  }
                }}>
                {reporting
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={styles.modalSubmitText}>{t('btn.submit', 'Submit Report')}</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Basic Info ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.basic', 'Basic Details')}</Text>
        <InfoRow icon="person-outline" label={t('field.gender', 'Gender')} value={tv('gender', profile.gender)} />
        <InfoRow icon="calendar-outline" label={t('field.dob', 'Date of Birth')} value={profile.dob} />
        <InfoRow icon="heart-outline" label={t('field.maritalStatus', 'Marital Status')} value={tv('maritalStatus', profile.maritalStatus)} />
        <InfoRow icon="globe-outline" label={t('field.religion', 'Religion')} value={tv('religion', profile.religion)} />
      </View>

      {/* ── Appearance & Astrology ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.appearance', 'Appearance')}</Text>
        <InfoRow icon="resize-outline" label={t('field.personHeight', 'Height')} value={profile.personHeight} />
        <InfoRow icon="body-outline" label={t('field.bodyType', 'Body Type')} value={profile.bodyType} />
        <InfoRow icon="water-outline" label={t('field.bloodGroup', 'Blood Group')} value={profile.bloodGroup} />
        <InfoRow icon="chatbubble-ellipses-outline" label={t('field.motherTongue', 'Mother Tongue')} value={profile.motherTongue} />
        <InfoRow icon="location-outline" label={t('field.nativePlace', 'Native Place')} value={profile.nativePlace} />
        <InfoRow icon="location-outline" label={t('field.birthPlace', 'Birth Place')} value={profile.birthPlace} />
        <InfoRow icon="time-outline" label={t('field.birthTime', 'Birth Time')} value={profile.dobTime} />
        <InfoRow icon="document-text-outline" label={t('field.kundliAvailable', 'Kundli Available')} value={bool(profile.kundliAvailable)} />
        <InfoRow icon="star-outline" label={t('field.nakshatra', 'Nakshatra')} value={profile.nakshatra} />
        <InfoRow icon="planet-outline" label={t('field.rashi', 'Rashi')} value={profile.rashi} />
      </View>

      {/* ── Education ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.education', 'Education')}</Text>
        <InfoRow icon="school-outline" label={t('field.highestEducation', 'Highest Education')} value={profile.highestEducation} />
        <InfoRow icon="school-outline" label={t('field.eduSubCategory', 'Education Specialization')} value={profile.eduSubCategory} />
        <InfoRow icon="business-outline" label={t('field.instituteName', 'Institute / College')} value={profile.instituteName} />
        <InfoRow icon="school-outline" label={t('field.otherDegree', 'Any Other Degree')} value={profile.otherDegree} />
      </View>

      {/* ── Career ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.career', 'Career')}</Text>
        <InfoRow icon="briefcase-outline" label={t('field.job', 'Occupation')} value={profile.job} />
        <InfoRow icon="briefcase-outline" label={t('field.jobSector', 'Job Sector')} value={profile.jobSector} />
        <InfoRow icon="briefcase-outline" label={t('field.jobType', 'Job Type')} value={tv('jobType', profile.jobType)} />
        <InfoRow
          icon="cash-outline"
          label={t('field.annualIncome', 'Annual Income')}
          value={incomeRange(profile.annualIncome)}
        />
        <InfoRow icon="business-outline" label={t('field.workAddress', 'Work Address')} value={profile.workAddress} />
      </View>

      {/* ── Community ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.caste', 'Caste / Community')}</Text>
        <InfoRow icon="layers-outline" label={t('field.caste', 'Caste')} value={profile.caste} />
        <InfoRow icon="layers-outline" label={t('field.casteSubType1', 'Subcaste')} value={profile.casteSubType1} />
        <InfoRow icon="layers-outline" label={t('field.surname', 'Surname')} value={profile.surname} />
        <InfoRow icon="git-branch-outline" label={t('field.gotra', 'Gotra')} value={profile.gotra} />
        <InfoRow icon="git-branch-outline" label={t('field.nadi', 'Nadi')} value={profile.nadi} />
        <InfoRow icon="star-outline" label={t('field.manglik', 'Manglik')} value={bool(profile.manglik)} />
      </View>

      {/* ── Family Background ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.family', 'Family')}</Text>
        <InfoRow icon="person-outline" label={t('field.fatherName', "Father's Name")} value={profile.fatherName} />
        <InfoRow icon="person-outline" label={t('field.motherName', "Mother's Name")} value={profile.motherName} />
        <InfoRow icon="checkmark-circle-outline" label={t('field.fatherAlive', 'Father Alive')} value={bool(profile.fatherAlive)} />
        <InfoRow icon="checkmark-circle-outline" label={t('field.motherAlive', 'Mother Alive')} value={bool(profile.motherAlive)} />
        <InfoRow icon="briefcase-outline" label={t('field.fatherJobType', "Father's Occupation")} value={tv('jobType', profile.fatherJobType)} />
        <InfoRow icon="briefcase-outline" label={t('field.motherJobType', "Mother's Occupation")} value={tv('jobType', profile.motherJobType)} />
        <InfoRow icon="people-outline" label={t('field.noOfBrothers', 'Brothers')} value={profile.noOfBrothers?.toString()} />
        <InfoRow icon="people-outline" label={t('field.noOfSisters', 'Sisters')} value={profile.noOfSisters?.toString()} />
        <InfoRow icon="people-outline" label={t('field.marriedBrothers', 'Married Brothers')} value={profile.marriedBrothers?.toString()} />
        <InfoRow icon="people-outline" label={t('field.marriedSisters', 'Married Sisters')} value={profile.marriedSisters?.toString()} />
        <InfoRow icon="home-outline" label={t('field.familyType', 'Family Type')} value={tv('familyType', profile.familyType)} />
        <InfoRow icon="trending-up-outline" label={t('field.familyStatus', 'Family Status')} value={tv('familyStatus', profile.familyStatus)} />
        <InfoRow icon="heart-circle-outline" label={t('field.familyValues', 'Family Values')} value={tv('familyValues', profile.familyValues)} />
      </View>

      {/* ── Lifestyle ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.lifestyle', 'Lifestyle')}</Text>
        <InfoRow icon="restaurant-outline" label={t('field.diet', 'Diet')} value={tv('diet', profile.diet)} />
        <InfoRow icon="flame-outline" label={t('field.smoking', 'Smoking')} value={tv('smoking', profile.smoking)} />
        <InfoRow icon="wine-outline" label={t('field.drinking', 'Drinking')} value={tv('drinking', profile.drinking)} />
        <InfoRow icon="accessibility-outline" label={t('field.speciallyAbled', 'Specially Abled')} value={bool(profile.speciallyAbled)} />
        {profile.speciallyAbled && (
          <InfoRow icon="information-circle-outline" label={t('field.specialAbility', 'Special Ability')} value={profile.specialAbility} />
        )}
      </View>

      {/* ── Address ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.address', 'Address')}</Text>
        <InfoRow icon="home-outline" label={t('field.address', 'Address')} value={profile.address} />
        <InfoRow icon="location-outline" label={t('field.district', 'District')} value={profile.district} />
        <InfoRow icon="map-outline" label={t('field.stateName', 'State')} value={profile.stateName} />
        <InfoRow icon="mail-outline" label={t('field.pinCode', 'Pin Code')} value={profile.pinCode} />
      </View>

      {/* ── Contact ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('section.contact', 'Contact')}</Text>

        {/* Phone */}
        <View style={styles.contactRow}>
          <Icon name="call-outline" size={18} color={THEME.primary} style={styles.infoIcon} />
          <View style={{ flex: 1 }}>
            <Text style={styles.infoLabel}>{t('field.contactNo', 'Contact No.')}</Text>
            {isOwner ? (
              <Text style={styles.infoValue}>{profile.contactNo ?? '—'}</Text>
            ) : contactPhone ? (
              <Text style={styles.infoValue}>{contactPhone}</Text>
            ) : (
              <View style={styles.contactRevealRow}>
                <Text style={styles.maskedValue}>+91 ••••••••••</Text>
                {profile.allowedToView !== false && !(profile.contactPrivacy && profile.connectedWithViewer === false) && (
                  <TouchableOpacity
                    style={styles.revealBtn}
                    onPress={() => revealContact('phone')}
                    disabled={loadingPhone}>
                    {loadingPhone
                      ? <ActivityIndicator size="small" color={THEME.primary} />
                      : <Text style={styles.revealBtnText}>{t('btn.show', 'Show')}</Text>}
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </View>

        {/* Email — hidden for Apple relay addresses */}
        {!(isOwner && profile.email?.endsWith('@privaterelay.appleid.com')) &&
         !(contactEmail?.endsWith('@privaterelay.appleid.com')) && (
        <View style={styles.contactRow}>
          <Icon name="mail-outline" size={18} color={THEME.primary} style={styles.infoIcon} />
          <View style={{ flex: 1 }}>
            <Text style={styles.infoLabel}>{t('field.email', 'Email')}</Text>
            {isOwner ? (
              <Text style={styles.infoValue}>{profile.email ?? '—'}</Text>
            ) : contactEmail ? (
              <Text style={styles.infoValue}>{contactEmail}</Text>
            ) : (
              <View style={styles.contactRevealRow}>
                <Text style={styles.maskedValue}>••••@••••.com</Text>
                {profile.allowedToView !== false && !(profile.contactPrivacy && profile.connectedWithViewer === false) && (
                  <TouchableOpacity
                    style={styles.revealBtn}
                    onPress={() => revealContact('email')}
                    disabled={loadingEmail}>
                    {loadingEmail
                      ? <ActivityIndicator size="small" color={THEME.primary} />
                      : <Text style={styles.revealBtnText}>{t('btn.show', 'Show')}</Text>}
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </View>
        )}

        {(isOwner || contactAltPhone !== null) && (
          <View style={styles.infoRow}>
            <Icon name="call-outline" size={18} color={THEME.primary} style={styles.infoIcon} />
            <View style={{ flex: 1 }}>
              <Text style={styles.infoLabel}>{t('field.otherContactNo', 'Alt. Contact No.')}</Text>
              {isOwner ? (
                <Text style={styles.infoValue}>{profile.otherContactNo ?? '—'}</Text>
              ) : (
                <Text style={styles.infoValue}>{contactAltPhone}</Text>
              )}
            </View>
          </View>
        )}

        {!isOwner && profile.allowedToView === false && (
          <View style={styles.contactLockedBanner}>
            <Icon name="lock-closed-outline" size={14} color="#92400E" />
            <Text style={styles.contactLockedText}>{t('msg.membershipRequiredContact', 'Membership required to view contact details')}</Text>
          </View>
        )}

        {!isOwner && !!profile.contactPrivacy && profile.connectedWithViewer === false && (
          <View style={styles.contactLockedBanner}>
            <Icon name="lock-closed-outline" size={14} color="#92400E" />
            <Text style={styles.contactLockedText}>{t('msg.contactPrivacyLocked', 'This profile shares contact details only after accepting your interest.')}</Text>
          </View>
        )}

        {/* LinkedIn Verified Badge */}
        {!isOwner && profile.linkedinVerified && (
          <View style={styles.socialSection}>
            <Text style={styles.socialSectionLabel}>LinkedIn</Text>
            <View style={styles.socialRow}>
              {contactLinkedinName ? (
                <View style={[styles.socialChip, styles.socialLinkedin]}>
                  <Icon name="logo-linkedin" size={16} color="#fff" />
                  <Text style={styles.socialChipText}>{contactLinkedinName}</Text>
                  <Icon name="checkmark-circle" size={14} color="#fff" />
                </View>
              ) : (
                <View style={[styles.socialChip, styles.socialChipLocked]}>
                  <Icon name="logo-linkedin" size={16} color={THEME.textLight} />
                  <Text style={[styles.socialChipText, { color: THEME.textLight }]}>LinkedIn Verified</Text>
                  <Icon name="lock-closed-outline" size={12} color={THEME.textLight} />
                </View>
              )}
            </View>
            {!contactLinkedinName && (
              <Text style={styles.socialUnlockHint}>Unlock contact details to see LinkedIn name</Text>
            )}
          </View>
        )}

      </View>

      {/* ── Kundali Compatibility ── */}
      {!isOwner && (
        <View style={styles.section}>
          <View style={styles.kundaliTitleRow}>
            <Icon name="star" size={18} color={THEME.primary} />
            <Text style={[styles.sectionTitle, { marginBottom: 0, color: THEME.primary }]}>
              {t('section.kundaliMatch', 'Kundali Compatibility')}
            </Text>
          </View>

          {!profile.nakshatra ? (
            <View style={styles.kundaliPlaceholder}>
              <Icon name="star-outline" size={32} color={THEME.border} />
              <Text style={styles.kundaliPlaceholderText}>
                This profile hasn't added their Nakshatra yet. Kundali matching requires both partners to add their birth star.
              </Text>
            </View>
          ) : loadingKundali ? (
            <ActivityIndicator color="#8B5CF6" style={{ paddingVertical: 24 }} />
          ) : kundaliMatch === null ? (
            <TouchableOpacity style={styles.kundaliCheckBtn} onPress={loadKundaliMatch}>
              <Icon name="star-outline" size={18} color="#8B5CF6" />
              <Text style={styles.kundaliCheckBtnText}>{t('btn.checkCompatibility', 'Check Compatibility')}</Text>
            </TouchableOpacity>
          ) : !kundaliMatch.kundaliAvailable ? (
            <View style={styles.kundaliPlaceholder}>
              <Icon name="person-add-outline" size={32} color={THEME.border} />
              <Text style={styles.kundaliPlaceholderText}>
                Add your Nakshatra in Edit Profile to see Kundali compatibility.
              </Text>
            </View>
          ) : (
            <>
              {/* Score circle */}
              <View style={styles.kundaliScoreRow}>
                <View style={[styles.kundaliCircle, {
                  borderColor: kundaliMatch.compatibility === 'EXCELLENT' ? '#10B981'
                    : kundaliMatch.compatibility === 'GOOD' ? '#3B82F6'
                    : kundaliMatch.compatibility === 'ACCEPTABLE' ? '#F59E0B' : '#EF4444',
                }]}>
                  <Text style={[styles.kundaliScoreNum, {
                    color: kundaliMatch.compatibility === 'EXCELLENT' ? '#10B981'
                      : kundaliMatch.compatibility === 'GOOD' ? '#3B82F6'
                      : kundaliMatch.compatibility === 'ACCEPTABLE' ? '#F59E0B' : '#EF4444',
                  }]}>{kundaliMatch.totalScore}</Text>
                  <Text style={styles.kundaliScoreDen}>/36</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <View style={[styles.kundaliCompatBadge, {
                    backgroundColor: kundaliMatch.compatibility === 'EXCELLENT' ? '#D1FAE5'
                      : kundaliMatch.compatibility === 'GOOD' ? '#DBEAFE'
                      : kundaliMatch.compatibility === 'ACCEPTABLE' ? '#FEF3C7' : '#FEE2E2',
                  }]}>
                    <Text style={[styles.kundaliCompatText, {
                      color: kundaliMatch.compatibility === 'EXCELLENT' ? '#065F46'
                        : kundaliMatch.compatibility === 'GOOD' ? '#1E40AF'
                        : kundaliMatch.compatibility === 'ACCEPTABLE' ? '#92400E' : '#991B1B',
                    }]}>
                      {kundaliMatch.compatibility.replace('_', ' ')}
                    </Text>
                  </View>
                  <Text style={styles.kundaliNakInfo} numberOfLines={2}>
                    {kundaliMatch.groomNakshatra} × {kundaliMatch.brideNakshatra}
                  </Text>
                  {(kundaliMatch.groomRashi || kundaliMatch.brideRashi) && (
                    <Text style={styles.kundaliRashiInfo} numberOfLines={2}>
                      {kundaliMatch.groomRashi} × {kundaliMatch.brideRashi}
                    </Text>
                  )}
                </View>
              </View>

              {/* 8 Koot bars */}
              {[
                { name: 'Nadi', score: kundaliMatch.nadiScore, max: 8 },
                { name: 'Bhakoot', score: kundaliMatch.bhakootScore, max: 7 },
                { name: 'Gana', score: kundaliMatch.ganaScore, max: 6 },
                { name: 'Graha Maitri', score: kundaliMatch.grahaMaitriScore, max: 5 },
                { name: 'Yoni', score: kundaliMatch.yoniScore, max: 4 },
                { name: 'Tara', score: kundaliMatch.taraScore, max: 3 },
                { name: 'Vasya', score: kundaliMatch.vasyaScore, max: 2 },
                { name: 'Varna', score: kundaliMatch.varnaScore, max: 1 },
              ].map(k => (
                <View key={k.name} style={styles.kootRow}>
                  <Text style={styles.kootName}>{k.name}</Text>
                  <View style={styles.kootBarBg}>
                    <View style={[styles.kootBarFill, {
                      width: `${(k.score / k.max) * 100}%` as any,
                      backgroundColor: k.score === k.max ? '#10B981' : k.score === 0 ? '#EF4444' : '#8B5CF6',
                    }]} />
                  </View>
                  <Text style={styles.kootScore}>{k.score}/{k.max}</Text>
                </View>
              ))}

              {/* Doshas */}
              {kundaliMatch.doshas.length > 0 && (
                <View style={styles.kundaliTagSection}>
                  {kundaliMatch.doshas.map((d, i) => (
                    <View key={i} style={styles.doshaTag}>
                      <Icon name="warning-outline" size={13} color="#991B1B" />
                      <Text style={styles.doshaTagText}>{d}</Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Strengths */}
              {kundaliMatch.strengths.length > 0 && (
                <View style={styles.kundaliTagSection}>
                  {kundaliMatch.strengths.map((s, i) => (
                    <View key={i} style={styles.strengthTag}>
                      <Icon name="checkmark-circle-outline" size={13} color="#065F46" />
                      <Text style={styles.strengthTagText}>{s}</Text>
                    </View>
                  ))}
                </View>
              )}
            </>
          )}
        </View>
      )}

      {/* ── About ── */}
      {profile.profileDetails ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('section.about', 'About')}</Text>
          <Text style={styles.about}>{profile.profileDetails}</Text>
        </View>
      ) : null}

    </ScrollView>
  );
};

export default ProfileDetailScreen;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: THEME.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  galleryContainer: { width, height: PHOTO_HEIGHT, backgroundColor: '#EDE0D8' },
  galleryPlaceholder: { alignItems: 'center', justifyContent: 'center' },
  galleryImage: { width, height: PHOTO_HEIGHT },
  photoBlurOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.25)', gap: 10,
  },
  photoBlurText: { color: '#fff', fontSize: 15, fontWeight: '700', textAlign: 'center', paddingHorizontal: 32 },
  dots: {
    position: 'absolute', bottom: 12, alignSelf: 'center',
    flexDirection: 'row', gap: 6,
  },
  dot: { width: 7, height: 7, borderRadius: 3.5, backgroundColor: 'rgba(255,255,255,0.5)' },
  dotActive: { backgroundColor: '#fff', width: 18, borderRadius: 4 },
  photoCountBadge: {
    position: 'absolute', top: 12, right: 12,
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: 'rgba(0,0,0,0.45)', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12,
  },
  photoCountText: { color: '#fff', fontSize: 13, fontWeight: '700' },
  nameCard: {
    backgroundColor: THEME.card, marginHorizontal: 16, marginTop: -24,
    borderRadius: 20, padding: 20,
    shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 }, elevation: 4,
  },
  nameRow: { flexDirection: 'row', alignItems: 'flex-start' },
  nameRowRight: { alignItems: 'flex-end', gap: 8 },
  shortlistCheckbox: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    borderWidth: 1.5, borderColor: THEME.primary, borderRadius: 20,
    paddingHorizontal: 12, paddingVertical: 5,
    backgroundColor: '#fff',
  },
  shortlistCheckboxActive: { backgroundColor: THEME.primary, borderColor: THEME.primary },
  shortlistCheckboxText: { fontSize: 13, fontWeight: '700', color: THEME.primary },
  shortlistCheckboxTextActive: { color: '#fff' },
  name: { fontSize: 24, fontWeight: '900', color: THEME.text },
  sub: { fontSize: 16, color: THEME.textLight, marginTop: 4 },
  ownerBtns: { flexDirection: 'row', gap: 10, marginTop: 16 },
  ownerBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 10, borderRadius: 10,
    borderWidth: 1.5, borderColor: THEME.primary, backgroundColor: '#FAE5E5',
  },
  ownerBtnText: { color: THEME.primary, fontWeight: '700', fontSize: 15 },
  section: {
    backgroundColor: THEME.card, marginHorizontal: 16, marginTop: 12,
    borderRadius: 16, padding: 18,
  },
  sectionTitle: { fontSize: 17, fontWeight: '800', color: THEME.text, marginBottom: 14 },
  infoRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  infoIcon: { marginRight: 12, marginTop: 2 },
  infoLabel: { fontSize: 12, color: THEME.textLight, textTransform: 'uppercase', letterSpacing: 0.5 },
  infoValue: { fontSize: 16, color: THEME.text, fontWeight: '600', marginTop: 2 },
  profileCode: { fontSize: 12, color: THEME.textLight, marginTop: 4, letterSpacing: 0.5 },
  about: { fontSize: 15, color: THEME.text, lineHeight: 23 },
  errorText: { color: THEME.textLight, fontSize: 17 },
  contactRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
  contactRevealRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 2 },
  maskedValue: { fontSize: 16, color: THEME.textLight, fontWeight: '600', letterSpacing: 1 },
  revealBtn: {
    paddingHorizontal: 14, paddingVertical: 5, borderRadius: 8,
    backgroundColor: '#FAE5E5', borderWidth: 1.5, borderColor: THEME.primary,
  },
  revealBtnText: { fontSize: 13, fontWeight: '700', color: THEME.primary },
  contactLockedBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#FFF3CD', borderRadius: 10, padding: 12, marginTop: 4,
  },
  contactLockedText: { flex: 1, fontSize: 13, color: '#92400E', lineHeight: 18 },
  interestBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, marginTop: 14, paddingVertical: 12, borderRadius: 12,
    backgroundColor: THEME.primary,
  },
  interestBtnSent: { backgroundColor: THEME.success },
  interestBtnText: { color: '#fff', fontWeight: '700', fontSize: 16 },
  interestBtnLocked: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, marginTop: 14, paddingVertical: 12, borderRadius: 12,
    backgroundColor: '#F5F5F5', borderWidth: 1, borderColor: THEME.border,
  },
  interestBtnLockedText: { fontSize: 14, color: THEME.textLight, fontWeight: '500' },
  viewsBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginHorizontal: 16, marginTop: 10, paddingHorizontal: 14, paddingVertical: 9,
    borderRadius: 10, backgroundColor: '#EEF2FF',
  },
  viewsBannerLow: { backgroundColor: '#FFF3CD' },
  viewsBannerText: { flex: 1, fontSize: 13, color: THEME.primary, fontWeight: '500' },
  viewsBannerTextLow: { color: '#92400E' },
  upgradeBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginTop: 24, paddingVertical: 13, paddingHorizontal: 28,
    borderRadius: 12, backgroundColor: THEME.primary,
  },
  upgradeBtnText: { color: '#fff', fontWeight: '700', fontSize: 16 },

  // Options bottom sheet (block / report)
  optionsOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end',
  },
  optionsSheet: {
    backgroundColor: THEME.card, borderTopLeftRadius: 20, borderTopRightRadius: 20,
    paddingBottom: 32, paddingTop: 8,
  },
  optionsHandle: {
    width: 40, height: 4, borderRadius: 2, backgroundColor: THEME.border,
    alignSelf: 'center', marginBottom: 12,
  },
  optionsItem: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    paddingHorizontal: 24, paddingVertical: 16,
  },
  optionsItemText: { fontSize: 16, fontWeight: '600', color: THEME.text },
  optionsDivider: { height: 1, backgroundColor: THEME.border, marginHorizontal: 24 },
  optionsCancelText: { fontSize: 16, fontWeight: '700', color: THEME.textLight },

  // Report modal
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  modalCard: {
    backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24,
    padding: 24, paddingBottom: 36,
  },
  modalTitle: { fontSize: 18, fontWeight: '900', color: THEME.text, marginBottom: 6 },
  modalSubtitle: { fontSize: 14, color: THEME.textLight, marginBottom: 18 },
  reportOption: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: THEME.border,
  },
  reportOptionSelected: {},
  reportOptionText: { fontSize: 15, color: THEME.text, flex: 1 },
  reportOptionTextSelected: { color: THEME.primary, fontWeight: '700' },
  modalBtns: { flexDirection: 'row', gap: 10, marginTop: 20 },
  modalCancelBtn: {
    flex: 1, paddingVertical: 13, borderRadius: 12,
    borderWidth: 1.5, borderColor: THEME.border,
    alignItems: 'center',
  },
  modalCancelText: { fontSize: 15, fontWeight: '700', color: THEME.textLight },
  modalSubmitBtn: {
    flex: 2, paddingVertical: 13, borderRadius: 12,
    backgroundColor: THEME.primary, alignItems: 'center',
  },
  modalSubmitBtnDisabled: { backgroundColor: THEME.border },
  modalSubmitText: { fontSize: 15, fontWeight: '700', color: '#fff' },

  // Kundali match
  kundaliTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 },
  kundaliPlaceholder: { alignItems: 'center', paddingVertical: 20, gap: 10 },
  kundaliPlaceholderText: { fontSize: 14, color: THEME.textLight, textAlign: 'center', lineHeight: 20, paddingHorizontal: 8 },
  kundaliCheckBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    paddingVertical: 12, borderRadius: 12, borderWidth: 1.5, borderColor: '#8B5CF6',
    backgroundColor: '#F5F3FF',
  },
  kundaliCheckBtnText: { fontSize: 15, fontWeight: '700', color: '#8B5CF6' },
  kundaliScoreRow: { flexDirection: 'row', alignItems: 'center', gap: 16, marginBottom: 16 },
  kundaliCircle: {
    width: 80, height: 80, borderRadius: 40, borderWidth: 4,
    alignItems: 'center', justifyContent: 'center',
  },
  kundaliScoreNum: { fontSize: 28, fontWeight: '900' },
  kundaliScoreDen: { fontSize: 12, color: THEME.textLight, fontWeight: '600', marginTop: -2 },
  kundaliCompatBadge: { alignSelf: 'flex-start', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, marginBottom: 6 },
  kundaliCompatText: { fontSize: 13, fontWeight: '800', letterSpacing: 0.5 },
  kundaliNakInfo: { fontSize: 13, color: THEME.text, fontWeight: '600' },
  kundaliRashiInfo: { fontSize: 12, color: THEME.textLight, marginTop: 2 },
  kootRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 8 },
  kootName: { fontSize: 12, color: THEME.textLight, width: 82, fontWeight: '500' },
  kootBarBg: { flex: 1, height: 6, backgroundColor: THEME.border, borderRadius: 3, overflow: 'hidden' },
  kootBarFill: { height: 6, borderRadius: 3 },
  kootScore: { fontSize: 12, color: THEME.text, fontWeight: '700', width: 30, textAlign: 'right' },
  kundaliTagSection: { gap: 6, marginTop: 10 },
  doshaTag: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 6,
    backgroundColor: '#FEE2E2', borderRadius: 8, padding: 8,
  },
  doshaTagText: { fontSize: 13, color: '#991B1B', flex: 1, lineHeight: 18 },
  strengthTag: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 6,
    backgroundColor: '#D1FAE5', borderRadius: 8, padding: 8,
  },
  strengthTagText: { fontSize: 13, color: '#065F46', flex: 1, lineHeight: 18 },
  // Social links
  socialSection: { marginTop: 14, paddingTop: 12, borderTopWidth: 1, borderTopColor: THEME.border },
  socialSectionLabel: { fontSize: 12, color: THEME.textLight, fontWeight: '600', marginBottom: 8 },
  socialRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  socialChip: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20,
  },
  socialChipText: { fontSize: 13, fontWeight: '700', color: '#fff' },
  socialChipLocked: { backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: THEME.border },
  socialLinkedin: { backgroundColor: '#0A66C2' },
  socialUnlockHint: { fontSize: 11, color: THEME.textLight, marginTop: 8, fontStyle: 'italic' },
});
